import React, { useState } from 'react';
import { useTheme } from '../../theme/ThemeContext';
import './OnboardingModal.css';

/**
 * OnboardingModal - User profile completion form
 * Displayed after login if onboarding_completed is false
 * Collects: Name, Age, Sex
 */
const OnboardingModal = ({ isOpen, onSubmit, isLoading = false, error = null }) => {
  const { theme } = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    sex: ''
  });
  const [validationErrors, setValidationErrors] = useState({});

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }

    if (!formData.age.trim()) {
      errors.age = 'Age is required';
    } else if (isNaN(formData.age) || formData.age < 1 || formData.age > 150) {
      errors.age = 'Please enter a valid age (1-150)';
    }

    if (!formData.sex) {
      errors.sex = 'Please select your gender';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear validation error for this field when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    await onSubmit(formData);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="onboarding-overlay" style={{ backgroundColor: theme.overlay }}>
      <div className="onboarding-modal" style={{
        backgroundColor: theme.background.primary,
        color: theme.text.primary,
        borderColor: theme.border.input
      }}>
        {/* Header */}
        <div className="onboarding-header">
          <h2>Welcome! Let's Get You Started</h2>
          <p>Please complete your profile to continue</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="onboarding-error" style={{
            backgroundColor: theme.background.error,
            borderColor: theme.border.error,
            color: theme.text.error
          }}>
            <span className="error-icon">⚠️</span>
            <div>
              <p className="error-message">{error}</p>
              <p className="error-hint">Please try again</p>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="onboarding-form">
          {/* Name Field */}
          <div className="form-group">
            <label htmlFor="name">Full Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="John Doe"
              style={{
                backgroundColor: theme.background.input,
                borderColor: validationErrors.name ? theme.border.error : theme.border.input,
                color: theme.text.primary
              }}
              disabled={isLoading}
            />
            {validationErrors.name && (
              <span className="field-error" style={{ color: theme.text.error }}>
                {validationErrors.name}
              </span>
            )}
          </div>

          {/* Age Field */}
          <div className="form-group">
            <label htmlFor="age">Age</label>
            <input
              type="number"
              id="age"
              name="age"
              value={formData.age}
              onChange={handleChange}
              placeholder="31"
              min="1"
              max="150"
              style={{
                backgroundColor: theme.background.input,
                borderColor: validationErrors.age ? theme.border.error : theme.border.input,
                color: theme.text.primary
              }}
              disabled={isLoading}
            />
            {validationErrors.age && (
              <span className="field-error" style={{ color: theme.text.error }}>
                {validationErrors.age}
              </span>
            )}
          </div>

          {/* Sex/Gender Field */}
          <div className="form-group">
            <label htmlFor="sex">Gender</label>
            <select
              id="sex"
              name="sex"
              value={formData.sex}
              onChange={handleChange}
              style={{
                backgroundColor: theme.background.input,
                borderColor: validationErrors.sex ? theme.border.error : theme.border.input,
                color: formData.sex ? theme.text.primary : theme.text.muted
              }}
              disabled={isLoading}
            >
              <option value="">Select your gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
              <option value="Prefer not to say">Prefer not to say</option>
            </select>
            {validationErrors.sex && (
              <span className="field-error" style={{ color: theme.text.error }}>
                {validationErrors.sex}
              </span>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="onboarding-submit"
            style={{
              backgroundColor: theme.primary,
              color: theme.text.inverse,
              opacity: isLoading ? 0.7 : 1
            }}
            disabled={isLoading}
          >
            {isLoading ? 'Completing Profile...' : 'Complete Profile'}
          </button>
        </form>

        {/* Footer */}
        <p className="onboarding-footer" style={{ color: theme.text.muted }}>
          Your information is secure and will only be used to personalize your experience
        </p>
      </div>
    </div>
  );
};

export default OnboardingModal;
